import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import HttpService from '../services/httpService';
import { setLoading, setSnackInfo } from '../redux/actions/appAction';

const defaultForm = {
  status: '',
  colour: '',
  hexCode: '#22C55E',
  meaning: '',
};

const HrStatusColorCode = (props) => {
  const [rows, setRows] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [formData, setFormData] = useState(defaultForm);

  const isEditMode = useMemo(() => Boolean(editingItem?._id), [editingItem]);

  const resetForm = () => {
    setFormData(defaultForm);
    setEditingItem(null);
  };

  const fetchList = async () => {
    props.setLoading(true);
    try {
      const response = await HttpService.getHrStatusColorCodes();
      const list = Array.isArray(response?.data) ? response.data : [];
      setRows(list);
    } catch (error) {
      console.error('Error fetching HR status color codes:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error fetching HR status color codes',
        'error'
      );
      setRows([]);
    } finally {
      props.setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCreate = () => {
    resetForm();
    setOpenForm(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      status: item?.status || '',
      colour: item?.colour || '',
      hexCode: item?.hexCode || '#22C55E',
      meaning: item?.meaning || '',
    });
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    resetForm();
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!formData.status.trim()) return 'Status is required';
    if (!formData.colour.trim()) return 'Colour is required';
    if (!formData.hexCode.trim()) return 'Hex code is required';
    if (!/^#([0-9a-fA-F]{6})$/.test(formData.hexCode.trim())) {
      return 'Hex code must be in format #RRGGBB';
    }
    if (!formData.meaning.trim()) return 'Meaning is required';
    return '';
  };

  const handleSubmit = async () => {
    const validationMessage = validateForm();
    if (validationMessage) {
      props.setSnackInfo(validationMessage, 'error');
      return;
    }

    const payload = {
      status: formData.status.trim(),
      colour: formData.colour.trim(),
      hexCode: formData.hexCode.trim(),
      meaning: formData.meaning.trim(),
    };

    props.setLoading(true);
    try {
      if (isEditMode) {
        await HttpService.updateHrStatusColorCode(editingItem._id, payload);
        props.setSnackInfo('Status color code updated successfully', 'success');
      } else {
        await HttpService.createHrStatusColorCode(payload);
        props.setSnackInfo('Status color code created successfully', 'success');
      }
      handleCloseForm();
      await fetchList();
    } catch (error) {
      console.error('Error saving HR status color code:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error saving status color code',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  const handleOpenDelete = (item) => {
    setDeletingItem(item);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    if (!deletingItem?._id) return;

    props.setLoading(true);
    try {
      await HttpService.deleteHrStatusColorCode(deletingItem._id);
      props.setSnackInfo('Status color code deleted successfully', 'success');
      setOpenDelete(false);
      setDeletingItem(null);
      await fetchList();
    } catch (error) {
      console.error('Error deleting HR status color code:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error deleting status color code',
        'error'
      );
    } finally {
      props.setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          HR Status Color Code
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          style={{ backgroundColor: '#F47E46', color: '#fff' }}
          onClick={handleOpenCreate}
        >
          Add Status Color
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 700 }}>Sl. No.</TableCell>
              <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell style={{ fontWeight: 700 }}>Colour</TableCell>
              <TableCell style={{ fontWeight: 700 }}>Hex Code</TableCell>
              <TableCell style={{ fontWeight: 700 }}>Meaning</TableCell>
              <TableCell style={{ fontWeight: 700 }}>Preview</TableCell>
              <TableCell style={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No status color codes found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((item, idx) => (
                <TableRow key={item._id || idx} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{item.status || '-'}</TableCell>
                  <TableCell>{item.colour || '-'}</TableCell>
                  <TableCell>{item.hexCode || '-'}</TableCell>
                  <TableCell>{item.meaning || '-'}</TableCell>
                  <TableCell>
                    <Box
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        backgroundColor: item.hexCode || '#e0e0e0',
                        border: '1px solid #ddd',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenEdit(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        style={{ color: '#d32f2f' }}
                        onClick={() => handleOpenDelete(item)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? 'Edit Status Color Code' : 'Add Status Color Code'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gridGap={12}>
            <TextField
              label="Status"
              variant="outlined"
              size="small"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              fullWidth
            />
            <TextField
              label="Colour"
              variant="outlined"
              size="small"
              value={formData.colour}
              onChange={(e) => handleChange('colour', e.target.value)}
              fullWidth
            />
            <Box display="flex" alignItems="center" gridGap={12}>
              <TextField
                label="Hex Code"
                variant="outlined"
                size="small"
                value={formData.hexCode}
                onChange={(e) => handleChange('hexCode', e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Pick"
                type="color"
                variant="outlined"
                size="small"
                value={/^#([0-9a-fA-F]{6})$/.test(formData.hexCode) ? formData.hexCode : '#22C55E'}
                onChange={(e) => handleChange('hexCode', e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 120 }}
              />
            </Box>
            <TextField
              label="Meaning"
              variant="outlined"
              size="small"
              value={formData.meaning}
              onChange={(e) => handleChange('meaning', e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} color="default">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Status Color Code</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete status "{deletingItem?.status || '-'}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default connect(null, { setLoading, setSnackInfo })(HrStatusColorCode);
